/**
 * `routes` — Phase-2 GeoViewer route table per WP-E1.
 *
 * Mirrors `architecture.md` §3.2 lines 488–552 and the site map in
 * `navigation-map.md` §1. Every standalone path enumerated in the
 * binding §3.2 route table is present here. Surfaces whose final
 * implementation has not yet landed on `release/2026-q2-geoviewer`
 * resolve to `<RouteScaffold />` so the URL still resolves, the
 * deep-link grammar still dispatches, and screen-reader announcement
 * behaviour stays consistent (per dispatch §4 step 2).
 *
 * Bundle-size note (AC8): scaffold definitions are kept in the
 * `SCAFFOLD_DEFS` data tables so a single `RouteScaffold` factory call
 * is generated per route instead of duplicating long inline JSX, and
 * `IntakeOverlayRoute` is `React.lazy`-loaded so the converter
 * handoff package only ships in its own chunk.
 *
 * Special-case routes:
 *
 *   - `/intake/handoff` (DEC-19 + DEC-42): preserved as a real
 *     path-based route mapped to `<IntakeOverlayRoute />` (lazy).
 *   - `/workspace`: parent route renders `<WorkspaceShell />` (which
 *     wraps the existing `<AppShell />` plus an `<Outlet />` for
 *     surface-replacing children such as catalog / settings / help).
 *   - `/workspace` index, `/workspace/profile`, `/workspace/pointcloud`:
 *     element resolves to `<ViewerCanvasOutlet />` (a no-op) because
 *     the parent `<WorkspaceShell />` already renders the canvas; mode
 *     selection comes from `#mode=` per `architecture.md` §3.3. This
 *     is the documented deviation (AC10 "rationale for any deviation
 *     from §3.2 shape") — using `<WorkspaceShell />` for both parent
 *     AND child would mount a second `<AppShell />` inside the
 *     parent's `<Outlet />`.
 *   - Catch-all `path: '*'`: resolves to a `RouteScaffold(GVW-163)`
 *     per AC3.
 *
 * The dispatcher (`<Root />` + `<DeepLinkDispatcher />`) is mounted
 * above every top-level route so `#view=`, `#viewro=`, `#mode=`,
 * `?layer=`, `#copilot=`, `#audit=` are processed regardless of the
 * matched path.
 *
 * Cite:
 *   - dispatch `WP-E1-routes-foundation-1.md` §4 step 2.
 *   - `architecture.md` §3.2 + §3.3 + §3.5.
 *   - `navigation-map.md` §1 + §5 + §10.
 */

import { Suspense, lazy } from 'react';
import type { RouteObject } from 'react-router-dom';
import { Root } from './DeepLinkDispatcher';
import { WorkspaceShell, ViewerCanvasOutlet } from './WorkspaceShell';
import { RouteScaffold } from './RouteScaffold';

// Settings components (GVW-145..153)
import { SettingsLanding } from './screens/settings/SettingsLanding';
import { ShareLinkSettings } from './screens/settings/ShareLinkSettings';
import { CredentialsSettings } from './screens/settings/CredentialsSettings';
import { IntegrationsSettings } from './screens/settings/IntegrationsSettings';
import { CrsSettings } from './screens/settings/CrsSettings';
import { AccessibilitySettings } from './screens/settings/AccessibilitySettings';
import { ThreeDCanvasSettings } from './screens/settings/ThreeDCanvasSettings';
import { TelemetrySettings } from './screens/settings/TelemetrySettings';
import { NotificationSettings } from './screens/settings/NotificationSettings';
import { CostBudgetSettings } from './screens/settings/CostBudgetSettings';
import { CostConfirmRoute } from './screens/settings/CostConfirmRoute';

// Help components (GVW-155..158)
import { ShortcutMap } from './screens/help/ShortcutMap';
import { VersionPage } from './screens/help/VersionPage';
import { StatusPage } from './screens/help/StatusPage';
import { DocsLinks } from './screens/help/DocsLinks';

// Audit components (GVW-130..133)
import { AuditPage } from './screens/audit/AuditPage';
import { AuditRowPage } from './screens/audit/AuditRowPage';
import { EvidencePackPage } from './screens/audit/EvidencePackPage';
import { FullAuditPage } from './screens/audit/FullAuditPage';

// WP-E2 standalone state pages
import { NoOrgsScreen } from './screens/NoOrgsScreen';
import { EmptyWorkspaceScreen } from './screens/EmptyWorkspaceScreen';
import { ForbiddenScreen } from './screens/ForbiddenScreen';
import { NotFoundScreen } from './screens/NotFoundScreen';
import { ServerErrorScreen } from './screens/ServerErrorScreen';
import { MaintenanceScreen } from './screens/MaintenanceScreen';
import { DegradedScreen } from './screens/DegradedScreen';
import { OfflineScreen } from './screens/OfflineScreen';
import { DefenceSplashScreen } from './screens/DefenceSplashScreen';
import { ShareDecoderFailedScreen } from './screens/ShareDecoderFailedScreen';

// WP-E3 catalog components (GVW-120..125)
import { CatalogBrowse } from './screens/catalog/CatalogBrowse';
import { DatasetDetail } from './screens/catalog/DatasetDetail';
import { CatalogUpload } from './screens/catalog/CatalogUpload';
import { CatalogDragDrop } from './screens/catalog/CatalogDragDrop';
import { CatalogJob } from './screens/catalog/CatalogJob';
import { CatalogLineage } from './screens/catalog/CatalogLineage';

// WP-E12 Dubai demo landing
import { DubaiDemoLanding } from './screens/DubaiDemoLanding';

// WP-PP5 gTwin viewer route
import { TwinRoute } from './TwinRoute';
import { StructureStudioRoute } from './StructureStudioRoute';
import { ProjectsRoute } from './ProjectsRoute';
import { EstateAdminRoute } from './EstateAdminRoute';
import { PlatformLanding } from './PlatformLanding';
import { RasterDemoRoute } from './RasterDemoRoute';
import { PanoramaSplitRoute } from './PanoramaSplitRoute';
import { TerrainDemoRoute } from './TerrainDemoRoute';

const IntakeOverlayRoute = lazy(() =>
  import('./IntakeOverlayRoute').then((m) => ({ default: m.IntakeOverlayRoute })),
);
const PointCloudSidecarRoute = lazy(() =>
  import('./PointCloudSidecarRoute').then((m) => ({ default: m.PointCloudSidecarRoute })),
);

interface ScaffoldDef {
  path: string;
  gvwId: string;
  title: string;
  wpId: string;
}

/**
 * Helper that materialises a `RouteScaffold` from a definition, keeping
 * the JSX call site singular. Reduces bundle-size cost vs. inlining
 * `<RouteScaffold ... />` per route.
 *
 * - `routePath` is the path field on the resulting `RouteObject`
 *   (relative or absolute depending on the parent context).
 * - `displayRoute` is the canonical absolute URL the scaffold shows
 *   (drives `data-route` and the screen narration).
 */
function scaffoldRoute(
  def: ScaffoldDef,
  routePath: string,
  displayRoute: string,
): RouteObject {
  return {
    path: routePath,
    element: (
      <RouteScaffold
        gvwId={def.gvwId}
        route={displayRoute}
        title={def.title}
        wpId={def.wpId}
      />
    ),
  };
}

// ---------------------------------------------------------------------------
// A — consumed shell standalone routes (architecture.md §3.2 lines 489–496).
// ---------------------------------------------------------------------------

const SIGNIN_AND_ONBOARDING: ReadonlyArray<ScaffoldDef> = [
  { path: 'signin', gvwId: 'GVW-001', title: 'Sign in', wpId: 'WP-E2' },
  { path: 'signin/mfa', gvwId: 'GVW-002', title: 'MFA challenge', wpId: 'WP-E2' },
  { path: 'signin/passkey-enroll', gvwId: 'GVW-003', title: 'Passkey enrolment', wpId: 'WP-E2' },
  { path: 'signin/conditional-access-denied', gvwId: 'GVW-004', title: 'Conditional access denied', wpId: 'WP-E2' },
  { path: 'signin/conditional-access-explain', gvwId: 'GVW-177', title: 'Conditional access explained', wpId: 'WP-E2' },
  { path: 'onboarding/no-orgs', gvwId: 'GVW-008', title: 'No organizations bound', wpId: 'WP-E2' },
  { path: 'signout/confirm', gvwId: 'GVW-014', title: 'Sign out', wpId: 'WP-E2' },
];

// ---------------------------------------------------------------------------
// Workspace child routes (architecture.md §3.2 lines 503–533).
// `index` (GVW-005/030), `profile` (GVW-070), `pointcloud` (GVW-080) all
// resolve to ViewerCanvasOutlet because the parent WorkspaceShell already
// renders AppShell — see file-level docstring deviation note.
// ---------------------------------------------------------------------------

// Remaining scaffolded routes (not implemented in other WPs)
const WORKSPACE_SCAFFOLDS: ReadonlyArray<ScaffoldDef> = [
  { path: 'share', gvwId: 'GVW-105', title: 'Share link', wpId: 'WP-E10' },
  { path: 'compare', gvwId: 'GVW-091', title: 'Scenario compare', wpId: 'WP-E6' },
];

const workspaceChildren: RouteObject[] = [
  { index: true, element: <ViewerCanvasOutlet /> }, // GVW-005 / 030
  { path: 'profile', element: <ViewerCanvasOutlet /> }, // GVW-070
  { path: 'pointcloud', element: <ViewerCanvasOutlet /> }, // GVW-080
  // GVW-161 Empty workspace (added per dispatch step 3)
  { path: 'empty', element: <EmptyWorkspaceScreen /> },
  
  // Settings routes (WP-E4) - swapped from RouteScaffold to real components
  { path: 'settings', element: <SettingsLanding /> }, // GVW-145
  { path: 'settings/share-link', element: <ShareLinkSettings /> }, // GVW-146
  { path: 'settings/credentials', element: <CredentialsSettings /> }, // GVW-147
  { path: 'settings/integrations', element: <IntegrationsSettings /> }, // GVW-148
  { path: 'settings/crs', element: <CrsSettings /> }, // GVW-149
  { path: 'settings/accessibility', element: <AccessibilitySettings /> }, // GVW-150
  { path: 'settings/3d-canvas', element: <ThreeDCanvasSettings /> }, // GVW-151
  { path: 'settings/telemetry', element: <TelemetrySettings /> }, // GVW-152
  { path: 'settings/notifications', element: <NotificationSettings /> }, // GVW-153
  
  // Cost budget routes (WP-E9)
  { path: 'cost-budget', element: <CostBudgetSettings /> }, // Cost budget overview
  { path: 'cost-confirm', element: <CostConfirmRoute /> }, // Cost confirmation test route
  
  // Help routes (WP-E4) - swapped from RouteScaffold to real components
  { path: 'help/shortcuts', element: <ShortcutMap /> }, // GVW-155
  { path: 'help/version', element: <VersionPage /> }, // GVW-156
  { path: 'help/status', element: <StatusPage /> }, // GVW-157
  { path: 'help/docs', element: <DocsLinks /> }, // GVW-158
  
  // Catalog routes (WP-E3) - swapped from RouteScaffold to real components
  { path: 'catalog', element: <CatalogBrowse /> }, // GVW-120
  { path: 'catalog/dataset/:id', element: <DatasetDetail /> }, // GVW-121
  { path: 'catalog/upload', element: <CatalogUpload /> }, // GVW-122
  { path: 'catalog/drag-drop', element: <CatalogDragDrop /> }, // GVW-123
  { path: 'catalog/job/:id', element: <CatalogJob /> }, // GVW-124
  { path: 'catalog/lineage/:id', element: <CatalogLineage /> }, // GVW-125
  
  // Audit routes (WP-E8) - swapped from RouteScaffold to real components
  { path: 'audit', element: <AuditPage /> }, // GVW-130
  { path: 'audit/row/:id', element: <AuditRowPage /> }, // GVW-131
  { path: 'audit/evidence-pack', element: <EvidencePackPage /> }, // GVW-132
  { path: 'audit/full', element: <FullAuditPage /> }, // GVW-133
  
  // Remaining scaffolded routes (other WPs)
  ...WORKSPACE_SCAFFOLDS.map((def) =>
    scaffoldRoute(def, def.path, `/workspace/${def.path}`),
  ),
];

// ---------------------------------------------------------------------------
// Q — standalone state screens (architecture.md §3.2 lines 541–549).
// S — Phase-2 specific (line 538).
// ---------------------------------------------------------------------------

const STANDALONE_STATES: ReadonlyArray<ScaffoldDef> = [
  // Note: demo/dubai (GVW-175) replaced with real DubaiDemoLanding component below
  // Note: GVW-160..169 scaffolds replaced with real components below
];

/**
 * Top-level routes per `architecture.md` §3.2 lines 488–552.
 *
 * Wrapped under a single `<Root />` element so the deep-link
 * dispatcher is mounted exactly once for the entire route tree.
 */
export const routes: RouteObject[] = [
  {
    element: <Root />,
    children: [
      { path: '/', element: <PlatformLanding /> },
      ...SIGNIN_AND_ONBOARDING.map((def) =>
        scaffoldRoute(def, `/${def.path}`, `/${def.path}`),
      ),
      // B — workspace shell (root) and children.
      {
        path: '/workspace',
        element: <WorkspaceShell />,
        children: workspaceChildren,
      },
      // DEC-19 / DEC-42 — converter handoff intake overlay (preserved
      // as a real path-based route so WP-B3 tests stay green). Wrapped
      // in Suspense because IntakeOverlayRoute is lazy-loaded.
      {
        path: '/intake/handoff',
        element: (
          <Suspense fallback={null}>
            <IntakeOverlayRoute />
          </Suspense>
        ),
      },
      ...STANDALONE_STATES.map((def) =>
        scaffoldRoute(def, `/${def.path}`, `/${def.path}`),
      ),
      // WP-E12 Dubai demo landing (real implementation)
      { path: '/demo/dubai', element: <DubaiDemoLanding /> }, // GVW-175

      // WP-PP5 gTwin viewer route (RFC-108.1 scene hydration + preset switcher)
      { path: '/projects', element: <ProjectsRoute /> },
      { path: '/admin/estate', element: <EstateAdminRoute /> },
      // GeoTIFF/COG smoke test (TiTiler + MapLibre; no gTwin required)
      { path: '/demo/raster-cog', element: <RasterDemoRoute /> },
      // Standalone photo ⇄ depth panorama split inspector (own browser tab).
      { path: '/pano-split', element: <PanoramaSplitRoute /> },
      // Terrain DEM demo (Giro3D ElevationLayer via TiTiler; no gTwin required)
      { path: '/demo/terrain', element: <TerrainDemoRoute /> },
      {
        path: '/pointcloud/viewer',
        element: (
          <Suspense fallback={null}>
            <PointCloudSidecarRoute />
          </Suspense>
        ),
      },
      {
        path: '/t/:gtwinId',
        element: <TwinRoute />,
        errorElement: (
          <RouteScaffold
            gvwId="GVW-PP5-ERR"
            route="/t/:gtwinId"
            title="Project not found"
            subhead="Project not found"
            body="The requested project could not be found or loaded."
            wpId="WP-PP5"
          />
        ),
      },
      {
        path: '/studio/t/:gtwinId',
        element: <StructureStudioRoute />,
        errorElement: (
          <RouteScaffold
            gvwId="GVW-STUDIO-ERR"
            route="/studio/t/:gtwinId"
            title="Structure Studio error"
            body="The requested project could not be loaded in Structure Studio."
            wpId="WP-B-STUDIO"
          />
        ),
      },
      
      // WP-E2 standalone state pages (real implementations)
      { path: '/no-orgs', element: <NoOrgsScreen /> }, // GVW-160
      { path: '/403', element: <ForbiddenScreen /> }, // GVW-162
      { path: '/404', element: <NotFoundScreen /> }, // GVW-163
      { path: '/500', element: <ServerErrorScreen /> }, // GVW-164
      { path: '/maintenance', element: <MaintenanceScreen /> }, // GVW-165
      { path: '/degraded', element: <DegradedScreen /> }, // GVW-166
      { path: '/offline', element: <OfflineScreen /> }, // GVW-167
      { path: '/defence-splash', element: <DefenceSplashScreen /> }, // GVW-168
      { path: '/share-decoder-failed', element: <ShareDecoderFailedScreen /> }, // GVW-169
      // Catch-all → GVW-163 NotFoundScreen (real implementation)
      {
        path: '*',
        element: <NotFoundScreen />,
      },
    ],
  },
];
